import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import UserContext from "../../context/user";

const useUpdateFavoritePolicies = ({
  doInvalidateAllPolicies,
  doInvalidateMyPolicies,
  doOptimisticUpdateAllPolicies,
  doOptimisticUpdateMyPolicies,
  currentQueryKey,
}) => {
  const { callApi } = useContext(UserContext);
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, isDelete }) =>
      callApi(`/policy/favorites/${id}`, {
        method: isDelete ? "DELETE" : "POST",
      }),
    {
      onSuccess: (_data, { id, isDelete }) => {
        if (doInvalidateAllPolicies) {
          //Invalidate cache for all polities
          console.log("Invalidating cache for", ["policies-all"]);
          queryClient.removeQueries(["policies-all"]);
        } else {
          //Invalidate cache for all polities except current
          queryClient.removeQueries({
            predicate: (q) => {
              const doInvalidate =
                q.queryKey[0] === "policies-all" &&
                JSON.stringify(q.queryKey[3]) !== JSON.stringify(currentQueryKey[3]);
              if (doInvalidate) {
                console.log("Invalidating cache for query", q.queryKey);
              }
              return doInvalidate;
            },
          });
        }
        if (doInvalidateMyPolicies) {
          //Invalidate cache for my polities
          console.log("Invalidating cache for", ["policies-my"]);
          queryClient.removeQueries(["policies-my"]);
        } else {
          //Invalidate cache for my polities except current
          queryClient.removeQueries({
            predicate: (q) => {
              const doInvalidate =
                q.queryKey[0] === "policies-my" &&
                (JSON.stringify(q.queryKey[3]) !== JSON.stringify(currentQueryKey[3]) ||
                  !q.queryKey[4]);
              if (doInvalidate) {
                console.log("Invalidating cache for query", q.queryKey);
              }
              return doInvalidate;
            },
          });
        }
        // Perform optimistic update on current query (all)
        if (doOptimisticUpdateAllPolicies) {
          queryClient.setQueryData(currentQueryKey, (oldData) => {
            const newData = oldData?.pages.map((page) => ({
              ...page,
              data: page.data.map((item) => {
                if (item.masterID === id) {
                  return {
                    ...item,
                    bookmarked: !isDelete,
                  };
                } else {
                  return item;
                }
              }),
            }));
            return {
              ...oldData,
              pages: newData,
            };
          });
        }
        // Perform optimistic update on current query (my)
        if (doOptimisticUpdateMyPolicies && isDelete) {
          const pageSize = currentQueryKey[1];
          queryClient.setQueryData(currentQueryKey, (oldData) => {
            const allData = oldData?.pages.map((page) => page.data).flat();
            const filteredData = allData.filter((item) => item.masterID !== id);
            const pages = oldData.pages
              .map((page, index) => ({
                ...page,
                resultsAvailableCount: page.resultsAvailableCount - 1,
                data: filteredData.slice(index * pageSize, (index + 1) * pageSize),
              }))
              .filter((page) => !!page.data.length);
            return {
              pageParams: oldData.pageParams.slice(0, pages.length),
              pages,
            };
          });
        }
      },
    }
  );
};

export default useUpdateFavoritePolicies;
